<template>
    <q-card flat bordered class="point-editor">
        <q-card-section>
            <div class="row items-center q-mb-md">
                <q-icon name="place" size="sm" class="q-mr-sm" />
                <div class="text-subtitle1 text-weight-medium">{{ local.name || "Point" }}</div>
                <q-space />
                <q-btn flat round dense icon="close" color="black" @click="$emit('cancel')">
                    <q-tooltip>Fermer sans enregistrer</q-tooltip>
                </q-btn>
            </div>

            <q-input outlined dense label="Nom du point" v-model="local.name" @update:model-value="emitPreview" />
            <br>
            <q-input outlined dense v-model="local.color" label="Couleur" @update:model-value="emitPreview">
                <template v-slot:append>
                    <q-icon name="colorize" class="cursor-pointer">
                        <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                            <q-color v-model="local.color" @update:model-value="emitPreview" />
                        </q-popup-proxy>
                    </q-icon>
                </template>
            </q-input>
            <br>
            <label>Taille</label>
            <q-slider v-model="local.size" :min="5" :max="100" :step="1" color="light-green" dense label-always
                @update:model-value="emitPreview" />
            <br>
            <q-input v-model="local.note" outlined dense label="Note" type="textarea" />
            <br>
            <label><b>Style du nom</b></label>
            <q-input outlined dense v-model="local.label_color" label="Couleur du texte"
                @update:model-value="emitPreview">
                <template v-slot:append>
                    <q-icon name="format_color_text" class="cursor-pointer">
                        <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                            <q-color v-model="local.label_color" @update:model-value="emitPreview" />
                        </q-popup-proxy>
                    </q-icon>
                </template>
            </q-input>
            <br>
            <q-input outlined dense v-model.number="local.label_size" label="Taille du texte" type="number" :min="8"
                :max="72" @update:model-value="emitPreview" />
            <br>
            <q-select outlined dense v-model="local.label_font" :options="fontOptions" label="Police du texte"
                @update:model-value="emitPreview" />
            <br>
            <label>Forme du point</label>
            <q-btn-toggle v-model="local.shape" toggle-color="primary" :options="[
                { label: 'Cercle', value: 'circle', icon: 'circle' },
                { label: 'Carré', value: 'square', icon: 'crop_square' },
                { label: 'Image', value: 'image', icon: 'image' }
            ]" @update:model-value="emitPreview" />
            <br>
            <template v-if="local.shape === 'image'">
                <FileSelect clearable :options="options" option-label="title" option-value="id" emit-value map-options
                    outlined dense v-model="local.shape_image_id" label="Image du point (le point prend sa forme)"
                    @update:model-value="emitPreview" />
                <br>
            </template>
            <q-toggle v-model="local.hover_animation" label="Animation au survol (le point se soulève)"
                color="secondary" dense />
            <br>
            <label><b>Illustration du point</b></label>
            <FileSelect clearable :options="options" option-label="title" option-value="id" emit-value map-options
                outlined dense v-model="local.resource_id" label="Fichier associé au point"
                @update:model-value="emitPreview" />
            <br>
            <q-btn-toggle v-model="local.image_position" toggle-color="primary" dense :options="[
                { label: 'Haut', value: 'top' },
                { label: 'Bas', value: 'bottom' },
                { label: 'Droite', value: 'right' },
                { label: 'Gauche', value: 'left' },
                { label: 'Intérieur', value: 'inside' },
                { label: 'Vide', value: '' }
            ]" @update:model-value="emitPreview" />
            <br>
            <br>
            <div class="row items-center q-gutter-sm">
                <q-btn color="secondary" label="Enregistrer" @click="$emit('save', { ...local })" />
                <q-btn color="black" flat label="Annuler" @click="$emit('cancel')" />
                <q-space />
                <q-btn color="red" flat icon="delete" @click="$emit('delete')">
                    <q-tooltip>Supprimer le point</q-tooltip>
                </q-btn>
            </div>
        </q-card-section>
    </q-card>
</template>

<script>
import { reactive, watch } from "vue";
import FileSelect from "./FileSelect.vue";

// Formulaire UNIQUE des propriétés d'un point (création affinée et
// modification) — remplace l'ancien duo formulaire colonne 3 / popup.
// Travaille sur une copie locale ; le parent persiste au « save » et applique
// l'aperçu live au « preview ».
export default {
    components: { FileSelect },
    props: {
        point: { type: Object, required: true },
        options: { type: Array, default: () => [] },
    },
    emits: ["save", "cancel", "delete", "preview", "dirty"],
    setup(props, { emit }) {
        const fontOptions = ['Arial', 'Verdana', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Tahoma', 'Trebuchet MS'];
        const fromPoint = (p) => ({
            name: p.name || "",
            color: p.color || "black",
            size: p.size || 10,
            note: p.note || "",
            shape: p.shape || "circle",
            shape_image_id: p.shape_image_id || null,
            hover_animation: p.hover_animation || false,
            label_color: p.label_color || "#ffffff",
            label_size: p.label_size || 16,
            label_font: p.label_font || "Arial",
            resource_id: p.resource_id || null,
            image_position: p.image_position || "",
        });
        const local = reactive(fromPoint(props.point));
        const isDirty = () => JSON.stringify(fromPoint(props.point)) !== JSON.stringify({ ...local });
        // Ne réinitialiser la copie locale que si on change de point ; un simple
        // re-fetch du même point (visibilité, duplication…) ne doit pas écraser
        // des modifications non enregistrées.
        watch(() => props.point, (p, old) => {
            if (!old || p.id !== old.id || !isDirty()) Object.assign(local, fromPoint(p));
        });
        // Quitter la forme « image » efface l'image de forme
        watch(() => local.shape, (v) => { if (v !== "image") local.shape_image_id = null; });
        // Signaler au parent les modifs non enregistrées
        watch(local, () => {
            emit("dirty", isDirty());
        });
        const emitPreview = () => emit("preview", { ...local });
        return { local, fontOptions, emitPreview };
    },
};
</script>

<style scoped>
.point-editor {
    background-color: #fafafa;
    border-radius: 0;
}
</style>
